import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  QueryErrorState,
  SearchInput,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import { useSubmissionList } from '../api/queries'
import { NewSubmissionDrawer } from '../components/NewSubmissionDrawer'
import { SubmissionDetailDrawer } from '../components/SubmissionDetailDrawer'
import { useHasAnyPermission, usePermission } from '@/shell/module-system'
import { ORDER_TYPE_LABELS, type OrderType, type SubmissionSummary, type SubmissionStatus, type VendorStatus } from '../api/types'
import { displayStatus } from '../utils/status-meta'
import { parseDateOnly } from '@/lib/utils/date-only'

const STATUS_OPTIONS: Array<{ value: SubmissionStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'received', label: 'Received' },
  { value: 'validating', label: 'Validating' },
  { value: 'validated', label: 'Validated' },
  { value: 'posting', label: 'Posting' },
  { value: 'posted', label: 'Posted' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'post_failed', label: 'Post Failed' },
  { value: 'validation_failed', label: 'Validation Failed' },
]

// Vendors filter against their own simplified 2-bucket vendor_status,
// client-side — a vendor's own submission list is small, and the internal
// status filter options above would otherwise leak words like "Post Failed"
// (or "Rejected") into a dropdown even a vendor with no matching rows would see.
const VENDOR_STATUS_OPTIONS: Array<{ value: VendorStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'posted', label: 'Posted' },
]

export function UvivaSubmissionsPage() {
  const isAdmin = usePermission('admin')
  const isOps = useHasAnyPermission(['operations', 'purchasing'])
  const isUviva = usePermission('uviva')
  const canApprove = isAdmin || isOps
  // Regulatory (CofA) + finance (Invoice) get read-only visibility — they can
  // view the board and download documents but never create a submission or
  // attach/replace documents.
  const canCreate = isAdmin || isOps || isUviva

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<SubmissionStatus | ''>('')
  const [vendorStatusFilter, setVendorStatusFilter] = useState<VendorStatus | ''>('')
  const [orderTypeFilter, setOrderTypeFilter] = useState<OrderType | ''>('')
  const [newOpen, setNewOpen] = useState(false)
  const [selected, setSelected] = useState<SubmissionSummary | null>(null)

  const { data, isLoading, isFetching, isError, error, refetch } = useSubmissionList({
    // Vendors filter client-side against vendor_status instead (see rows
    // below) — the server-side status param is the internal enum.
    status: isUviva ? undefined : statusFilter || undefined,
    order_type: orderTypeFilter || undefined,
    limit: 100,
  })

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    let items = data?.items ?? []
    if (isUviva && vendorStatusFilter) {
      items = items.filter((s) => s.vendor_status === vendorStatusFilter)
    }
    if (!needle) return items
    return items.filter(
      (s) =>
        s.shipment_ref.toLowerCase().includes(needle) ||
        (s.po_ref?.toLowerCase().includes(needle) ?? false) ||
        (s.order_ref?.toLowerCase().includes(needle) ?? false),
    )
  }, [data, search, isUviva, vendorStatusFilter])

  const columns = useMemo<ColumnDef<SubmissionSummary>[]>(
    () => [
      {
        id: 'shipment_ref',
        header: 'Shipment Ref',
        accessorKey: 'shipment_ref',
        cell: (ctx) => (
          <span className="text-role-data-primary">{ctx.getValue() as string}</span>
        ),
      },
      {
        id: 'order_type',
        header: 'Type',
        accessorKey: 'order_type',
        cell: (ctx) => {
          const ot = ctx.getValue() as OrderType
          return (
            <Chip
              variant={ot === 'uviva_direct' ? 'info' : ot === 'uvwh_intake' ? 'warning' : 'neutral'}
              size="xs"
            >
              {ORDER_TYPE_LABELS[ot]}
            </Chip>
          )
        },
      },
      {
        id: 'po_ref',
        header: 'PO Ref',
        accessorKey: 'po_ref',
        cell: (ctx) => (
          <span className="text-role-data-secondary">{(ctx.getValue() as string | null) ?? '—'}</span>
        ),
      },
      {
        id: 'invoice_number',
        header: 'Invoice #',
        accessorFn: (row) => {
          const invoiceDoc = row.attached_documents?.find((d) => d.doc_type === 'invoice')
          return invoiceDoc ? invoiceDoc.filename.replace(/\.pdf$/i, '') : null
        },
        cell: (ctx) => (
          <span className="text-role-data-secondary">{(ctx.getValue() as string | null) ?? '—'}</span>
        ),
      },
      {
        id: 'planned_delivery_date',
        header: 'Ship Date',
        accessorKey: 'planned_delivery_date',
        cell: (ctx) => {
          const v = ctx.getValue() as string | null
          if (!v) return <span className="text-role-metadata">—</span>
          // Date-only field (no time component) — parseDateOnly avoids the
          // UTC-midnight-vs-local-timezone off-by-one that new Date(v) hits
          // in every US timezone (2026-05-08 rendering as "May 7").
          return (
            <span className="text-role-metadata">
              {parseDateOnly(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )
        },
      },
      {
        id: 'lines',
        header: 'Lines',
        accessorFn: (row) => row.lines?.length ?? '—',
        cell: (ctx) => (
          <span className="text-role-table-cell">{ctx.getValue() as number}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: (ctx) => {
          const { label, variant } = displayStatus(ctx.row.original, isUviva)
          return (
            <Chip variant={variant} size="xs">
              {label}
            </Chip>
          )
        },
      },
      {
        id: 'notes',
        header: 'Notes',
        accessorKey: 'notes',
        cell: (ctx) => {
          const v = ctx.getValue() as string | null
          if (!v) return <span className="text-role-metadata">—</span>
          return (
            <span className="text-role-data-secondary line-clamp-1" title={v}>
              {v}
            </span>
          )
        },
      },
      {
        id: 'submitted_at',
        header: 'Submitted',
        accessorKey: 'submitted_at',
        cell: (ctx) => {
          const v = ctx.getValue() as string | null
          if (!v) return <span className="text-role-metadata">—</span>
          return (
            <span className="text-role-metadata">
              {new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )
        },
      },
    ],
    [isUviva],
  )

  return (
    <>
      <div className="flex flex-col h-full bg-canvas">
        <header className="flex items-center justify-between border-b border-border-subtle bg-surface px-6 py-3">
          <h1 className="text-role-page-title">Submissions</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'Refreshing…' : '⟳ Refresh'}
            </Button>
            {canCreate ? (
              <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
                + New
              </Button>
            ) : null}
          </div>
        </header>

        {/* Search bar — own full-width row */}
        <div className="border-b border-border-subtle bg-surface px-6 py-2">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search shipment ref, PO ref…"
            containerClassName="flex-1 max-w-none"
          />
        </div>

        <Toolbar className="border-b border-border-subtle bg-surface px-6 py-2">
          <ToolbarLeft>
            {isUviva ? (
              <select
                value={vendorStatusFilter}
                onChange={(e) => setVendorStatusFilter(e.target.value as VendorStatus | '')}
                className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
              >
                {VENDOR_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as SubmissionStatus | '')}
                className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
            <select
              value={orderTypeFilter}
              onChange={(e) => setOrderTypeFilter(e.target.value as OrderType | '')}
              className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">All types</option>
              <option value="spi_order">{ORDER_TYPE_LABELS.spi_order}</option>
              <option value="uvwh_intake">{ORDER_TYPE_LABELS.uvwh_intake}</option>
              <option value="uviva_direct">{ORDER_TYPE_LABELS.uviva_direct}</option>
            </select>
          </ToolbarLeft>
          <ToolbarRight>
            {data && (
              <span className="text-role-metadata">{data.total} total</span>
            )}
          </ToolbarRight>
        </Toolbar>

        <div className="flex-1 overflow-auto p-4">
          {isError ? (
            <QueryErrorState
              error={error}
              onRetry={() => void refetch()}
              subject="submissions"
            />
          ) : isLoading ? (
            <div className="text-role-metadata py-8 text-center">Loading submissions…</div>
          ) : rows.length === 0 ? (
            <EmptyState
              variant="no-data"
              title="No submissions found"
              description={search ? 'Try a different search term.' : 'Submit your first shipment to get started.'}
              action={
                !search && canCreate ? (
                  <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
                    + New Submission
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <DataTable
              moduleId="uviva-submissions"
              columns={columns}
              data={rows}
              onRowClick={(row) => setSelected(row)}
              // Vendors never see the internal exception states as "critical" —
              // their view already reads these as a calm "Received"; a red row
              // highlight would silently re-introduce the same alarm the
              // simplified status badge is meant to remove.
              criticalRowPredicate={
                isUviva
                  ? undefined
                  : (row) =>
                      row.status === 'pending_review' || row.status === 'post_failed' || row.status === 'validation_failed'
              }
            />
          )}
        </div>
      </div>

      <NewSubmissionDrawer open={newOpen} onClose={() => setNewOpen(false)} />
      <SubmissionDetailDrawer
        submission={selected}
        onClose={() => setSelected(null)}
        canApprove={canApprove}
        canManageDocuments={canCreate}
        isVendorView={isUviva}
      />
    </>
  )
}
